import { useEffect, useState , useCallback} from "react";
import axios from "axios";
import { API } from "@/App";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, ArrowRight, X, GraduationCap, PlayCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * GetStartedChecklist — the "Your Store Setup — X% Complete" card at the
 * top of the business dashboard. All completion states are COMPUTED
 * server-side from real signals (has_products, has_logo, has_published_pin,
 * etc.) — owners cannot fake progress by clicking a checkbox.
 *
 * Auto-hides once every item is complete OR the owner explicitly dismisses.
 */
export default function GetStartedChecklist({ business, onStartTour }) {
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissing, setDismissing] = useState(false);

    const fetchProgress = useCallback(async () => {
    if (!business?.business_id) return;
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/academy/progress/${business.business_id}`,
        { withCredentials: true },
      );
      setProgress(r.data);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, [business?.business_id]);

  useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  const dismiss = async () => {
    setDismissing(true);
    try {
      await axios.post(
        `${API}/academy/progress/${business.business_id}/checklist-dismiss`,
        { dismissed: true },
        { withCredentials: true },
      );
      setProgress((p) => ({ ...p, checklist_dismissed_at: new Date().toISOString() }));
      toast.success("Checklist hidden — you can re-open it any time from Business Academy.");
    } catch {
      toast.error("Couldn't hide checklist");
    } finally {
      setDismissing(false);
    }
  };

  if (loading || !progress) return null;
  // Hide when fully complete OR the owner dismissed it
  if (progress.percent_complete === 100) return null;
  if (progress.checklist_dismissed_at) return null;

  return (
    <div
      className="mb-6 rounded-2xl bg-gradient-to-br from-tbc-navy to-tbc-navy/95 text-white p-5 sm:p-6 border border-tbc-gold/30 shadow-lg"
      data-testid="dashboard-get-started-checklist"
    >
      <div className="flex items-start justify-between gap-2 mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-full bg-tbc-gold/20 text-tbc-gold flex items-center justify-center">
              <GraduationCap className="w-4 h-4" />
            </div>
            <p className="text-tbc-gold text-[10px] uppercase tracking-widest font-semibold">
              Business Academy
            </p>
          </div>
          <h2 className="font-heading text-xl sm:text-2xl">Get your business ready</h2>
          <p className="text-white/70 font-body text-sm mt-0.5">
            {progress.completed_count} of {progress.total_items} steps done ·{" "}
            {progress.percent_complete}% complete
          </p>
        </div>
        <div className="flex items-center gap-1">
          {onStartTour && (
            <Button
              size="sm"
              variant="outline"
              onClick={onStartTour}
              className="border-tbc-gold/50 text-tbc-gold bg-transparent hover:bg-tbc-gold hover:text-tbc-navy rounded-full text-xs h-8"
              data-testid="checklist-start-tour-btn"
            >
              <PlayCircle className="w-3.5 h-3.5 mr-1" />
              Start Tour
            </Button>
          )}
          <button
            type="button"
            onClick={dismiss}
            disabled={dismissing}
            className="w-8 h-8 rounded-full text-white/50 hover:text-white/90 flex items-center justify-center"
            aria-label="Hide checklist"
            data-testid="checklist-dismiss-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full h-2 rounded-full bg-white/10 overflow-hidden mb-5">
        <div
          className="h-full bg-tbc-gold transition-all duration-500"
          style={{ width: `${progress.percent_complete}%` }}
          role="progressbar"
          aria-valuenow={progress.percent_complete}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>

      {/* Checklist items */}
      <ul className="space-y-2">
        {progress.checklist.map((item) => (
          <li key={item.key}>
            <ChecklistRow item={item} />
          </li>
        ))}
      </ul>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-2 pt-4 border-t border-white/10">
        <p className="text-white/60 text-xs font-body">
          Need help? Visit the{" "}
          <Link to="/business-academy" className="text-tbc-gold underline">
            Business Academy
          </Link>{" "}
          any time.
        </p>
      </div>
    </div>
  );
}

function ChecklistRow({ item }) {
  const CTA = (
    <span className="inline-flex items-center gap-1 text-tbc-gold text-xs opacity-0 group-hover:opacity-100 transition-opacity">
      Do this <ArrowRight className="w-3 h-3" />
    </span>
  );
  const Icon = item.completed ? CheckCircle2 : Circle;
  const inner = (
    <div
      className={`group flex items-start gap-3 py-1.5 ${
        item.deep_link && !item.completed
          ? "cursor-pointer hover:bg-white/5 rounded-lg px-2 -mx-2"
          : ""
      }`}
      data-testid={`checklist-item-${item.key}`}
    >
      <Icon
        className={`w-5 h-5 mt-0.5 flex-none ${
          item.completed ? "text-tbc-gold" : "text-white/40"
        }`}
      />
      <div className="flex-1 min-w-0">
        <p
          className={`font-body text-sm ${
            item.completed ? "text-white/60 line-through" : "text-white"
          }`}
        >
          {item.label}
        </p>
        {!item.completed && item.description && (
          <p className="text-white/50 text-xs mt-0.5">{item.description}</p>
        )}
      </div>
      {!item.completed && item.deep_link && CTA}
    </div>
  );
  if (item.deep_link && !item.completed) {
    return <Link to={item.deep_link}>{inner}</Link>;
  }
  return inner;
}
